<template>
  <div
    id="my-container"
    v-on-clickaway="onClose"
  >
    <div class="my-3">
      <!-- Our triggering (target) element -->
      <button
        id="popover-reactive-1"
        ref="button"
        :disabled="popoverShow"
        class="filter-button"
      >
        {{ $t('filters') | capitalize }}
        <i class="fal fa-sliders-h" />
      </button>
    </div>

    <b-popover
      ref="popover"
      target="popover-reactive-1"
      triggers="click"
      :show.sync="popoverShow"
      placement="auto"
      container="my-container"
      @show="onShow"
    >
      <template v-slot:title>
        <b-button
          class="close"
          aria-label="Close"
          @click="onClose"
        >
          <span
            class="d-inline-block"
            aria-hidden="true"
          >&times;</span>
        </b-button>
        {{ title }}
      </template>

      <div>
        <div>
          <div>
            <div
              v-if="statuses"
              class="checkbox-container"
            >
              <label class="mb-1 b-form-group">{{ $t('selectStatus') | capitalize }}</label>
              <span
                v-for="[status, text] in Object.entries(statuses)"
                :key="status"
                class="mb-1"
              >
                <p-check
                  :checked="statusFilter === status"
                  class="p-icon p-curve p-pulse"
                  color="success"
                  @change="toggleStatus(status)"
                >
                  <template v-slot:extra>
                    <i class="icon fa fa-check"/>
                  </template>
                </p-check>
                <span>{{ text | capitalize }}</span>
              </span>
            </div>
          </div>

          <div>
            <div
              v-if="fileTypes"
              class="checkbox-container"
            >
              <label class="mb-1 b-form-group">{{ $t('selectFileType') | capitalize }}</label>
              <span
                v-for="[fileType, text] in Object.entries(fileTypes)"
                :key="fileType"
                class="mb-1"
              >
                <p-check
                  :checked="fileTypeFilter === fileType"
                  class="p-icon p-curve p-pulse"
                  color="success"
                  @change="toggleFiletype(fileType)"
                >
                  <template v-slot:extra>
                    <i class="icon fa fa-check"/>
                  </template>
                </p-check>
                <span>{{ text }}</span>
              </span>
            </div>
          </div>

          <div
            v-if="useTypesList"
            class="mb-4"
          >
            <label class="mb-1 b-form-group">{{ $t('useType') | capitalize }}</label>
            <multiselect
              v-model="selectedUseType"
              :close-on-select="true"
              :options="useTypesList"
              :placeholder="$t('selectUseType') | capitalize"
              :show-labels="false"
              label="name"
              track-by="name"
              class="filter-m-multiselect"
            />
          </div>

          <div
            v-if="membersList && isMlc"
            class="mb-4"
          >
            <label class="mb-1 b-form-group">{{ $t('member') | capitalize }}</label>
            <multiselect
              v-model="selectedMember"
              :close-on-select="true"
              :options="membersList"
              :placeholder="$t('selectMember') | capitalize"
              :show-labels="false"
              label="username"
              track-by="username"
              class="filter-m-multiselect"
            />
          </div>
        </div>
        <div
          v-if="membersList"
          class="mb-4"
        >
          <p-check
            :checked="showIneligibleModel"
            class="p-icon p-curve p-pulse ineligible-icon"
            color="success"
            @change="showIneligibleModel = !showIneligibleModel"
          >
            <template v-slot:extra>
              <i class="icon fa fa-check"/>
            </template>
          </p-check>
          <span>{{ $t('showIneligible') | capitalize }}</span>
        </div>
        <div class="flex justify-end mt-2">
          <b-button
            variant="none"
            size="sm"
            @click="onClearFilters"
          >
            {{ $t('clearFilters') | capitalize }}
          </b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<style lang="scss" scoped>
.checkbox-container {
  display: flex;
  flex-direction: column;
  padding: 4px;
}

.filter-button {
  border: 1px solid #0091ff;
  min-height: 40px;
  text-align: center;
  cursor: pointer;
  padding: 8px;
  margin-left: 15px;
  width: 100px;
  border-radius: 50px;
  color: #0091ff;
  background-color: white;
}

.ineligible-icon {
  z-index: 0;
}
</style>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"
import { mixin as clickaway } from "vue-clickaway"

export default {
  name: "FilterModule",
  mixins: [clickaway],
  computed: {
    ...mapState("ui", ["statusFilter", "fileTypeFilter", "useTypeFilter", "memberFilter", "showIneligible"]),
    ...mapGetters("soundsys", ["isMlc"])
  },
  data () {
    return {
      checkedStatus: null,
      checkedFileType: null,
      showIneligibleModel: false,
      selectedUseType: null,
      selectedMember:  null,
      popoverShow: false
    }
  },
  watch: {
    membersList (list) {
      if (this.memberFilter) this.setMemberFilter(list.filter(member => member.id == this.memberFilter.id)[0])
    },
    useTypesList (list) {
      if (this.useTypeFilter) this.setUseTypeFilter(list.filter(useType => useType.code == this.useTypeFilter.code)[0])
    },
    checkedStatus (val) {
      this.setStatusFilter(val)
      this.updateFilter("status", val)
    },
    checkedFileType (val) {
      this.setFileTypeFilter(val)
      this.updateFilter("file_type", val)
    },
    selectedUseType (val) {
      this.setUseTypeFilter(val)
      this.updateFilter("use_type", val?.code)
    },
    selectedMember (val) {
      this.setMemberFilter(val)
      this.updateFilter("member", val?.id)
    },
    showIneligibleModel (val) {
      this.setShowIneligible(val)
      this.updateFilter("show_ineligible", val)
    },
    statusFilter (val) {
      // Sets checkedStatus to null if the value of statusFiler is null.
      // This watcher is needed to set checkedStatus to null when the user
      // removes SelectedFilter by clicking on its delete icon.
      if (!val) this.checkedStatus = null
    },
    fileTypeFilter (val) {
      if (!val) this.checkedFileType = null
    },
  },
  methods: {
    ...mapMutations("ui", [
      "setStatusFilter",
      "setFileTypeFilter",
      "setUseTypeFilter",
      "setMemberFilter",
      "setShowIneligible"
    ]),
    toggleStatus (status) {
      if (this.checkedStatus === status) {
        this.checkedStatus = null
      } else {
        this.checkedStatus = status
      }
    },
    toggleFiletype (fileType) {
      if (this.checkedFileType === fileType) {
        this.checkedFileType = null
      } else {
        this.checkedFileType = fileType
      }
    },
    updateFilter (filterField, val) {
      let queryFilters = { ...this.$route.query }
      if (!val) {
        delete queryFilters[filterField]
      } else {
        queryFilters[filterField] = val
      }
      this.table.options.previousPage = null
      queryFilters.page = 1
      if (this.table) {
        this.$router.replace({
          name: this.$route.name,
          query: queryFilters
        })
      }
    },
    onClose () {
      this.popoverShow = false
    },
    onClearFilters () {
      this.checkedStatus = null
      this.checkedFileType = null
      this.selectedUseType = null
      this.selectedMember = null
      this.showIneligible - null
      this.popoverShow = false
    },
    onShow () {
      this.checkedStatus = this.statusFilter
      this.checkedFileType = this.fileTypeFilter
      this.selectedUseType = this.useTypeFilter
      this.selectedMember = this.memberFilter
      this.showIneligibleModel = this.showIneligible
    },
  },
  props: {
    statuses: Object,
    useTypesList: Array,
    membersList: Array,
    title: String,
    table: Object,
    fileTypes: Object,
  }
}
</script>
